export type ServiceId =
  | 'recovery'
  | 'carrier'
  | 'home'
  | 'furniture'
  | 'goods'
  | 'chiller'
  | 'construction'
  | 'business'
  | 'heavy'

export interface BookingSummary {
  pickup: string
  pickupTime: string
  dropoff: string
  dropoffTime: string
  routeHeadline: string
  vehicle: string
  vehicleNote: string
  crewLabel: string // e.g. "Required Helpers" / "Equipment Operator" / "Driver Only"
  crewValue: string
  crewNote: string
  baseFareLabel: string
  baseFare: number
  extraLabel: string
  extra: number
  insurance: number
  total: number
  totalUpper: number
  insuranceNote: string
  primaryAction: string // CTA on booking page
}

export interface SupplierCard {
  id: number
  name: string
  rating: number
  reviews: number
  eta: string
  price: number
  truck: string
  verified: boolean
  badge?: string
  features: string[]
  image: string
}

export interface AiChip {
  label: string
  next?: 'upload' | 'skip' | 'photos' | 'fragile' | 'help' | 'done'
}

export interface AiInitialMessage {
  text: string
  chips?: AiChip[]
  showUpload?: boolean
}

export interface AiFollowUps {
  uploadFirst: string
  uploadSecond: string
  uploadThird: string
  skipReply: { text: string; chips: AiChip[] }
  defaultReply: string
}

export interface ServiceConfig {
  id: ServiceId
  name: string
  shortName: string
  resultsHeading: string
  resultsSubheading: string
  trucksNearby: number
  defaultPrompt: string
  summary: BookingSummary
  suppliers: SupplierCard[]
  aiIntro: AiInitialMessage
  aiFollowUps: AiFollowUps
  aiTopic: string // shown in widget header subtitle
}

// -----------------------------------------------------------------------------
// Per-service data
// -----------------------------------------------------------------------------

export const SERVICE_DATA: Record<ServiceId, ServiceConfig> = {
  // ------------------------------------------------------ HOME MOVING
  home: {
    id: 'home',
    name: 'Home Moving',
    shortName: 'Home Move',
    resultsHeading: 'Movers available now',
    resultsSubheading: 'Full-service home relocation crews',
    trucksNearby: 12,
    defaultPrompt:
      'Move my 2 bedroom apartment from Marina to Downtown tomorrow',
    summary: {
      pickup: 'Dubai Marina',
      pickupTime: 'Tomorrow, 09:00 AM',
      dropoff: 'Downtown Dubai',
      dropoffTime: 'Est. arrival 10:30 AM',
      routeHeadline: 'Marina → Downtown',
      vehicle: '3-Ton Box Truck',
      vehicleNote: 'Perfect for 2BR apartments',
      crewLabel: 'Required Helpers',
      crewValue: '3 Movers',
      crewNote: 'Includes dismantling & assembly',
      baseFareLabel: 'Base Fare (3-Ton)',
      baseFare: 450,
      extraLabel: 'Helpers (x3)',
      extra: 300,
      insurance: 50,
      total: 800,
      totalUpper: 950,
      insuranceNote: 'Includes cargo insurance up to AED 50,000.',
      primaryAction: 'Find Available Movers',
    },
    suppliers: [
      {
        id: 1,
        name: 'FastTrack Movers',
        rating: 4.9,
        reviews: 342,
        eta: '15 mins',
        price: 850,
        truck: '3-Ton Box Truck',
        verified: true,
        badge: 'Best Match',
        features: ['3 Helpers', 'Disassembly Included', 'Padded Blankets'],
        image:
          'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?auto=format&fit=crop&q=80&w=400&h=250',
      },
      {
        id: 2,
        name: 'SafeMove UAE',
        rating: 4.7,
        reviews: 890,
        eta: '25 mins',
        price: 780,
        truck: '3-Ton Box Truck',
        verified: true,
        features: ['2 Helpers', 'GPS Tracking', 'Standard Insurance'],
        image:
          'https://images.unsplash.com/photo-1519003722824-194d4455a60c?auto=format&fit=crop&q=80&w=400&h=250',
      },
      {
        id: 3,
        name: 'Premium White Glove',
        rating: 5.0,
        reviews: 124,
        eta: '45 mins',
        price: 1180,
        truck: '5-Ton Closed Body',
        verified: true,
        features: ['4 Helpers', 'White Glove Service', 'Premium Insurance'],
        image:
          'https://images.unsplash.com/photo-1586864387967-d02ef85d93e8?auto=format&fit=crop&q=80&w=400&h=250',
      },
    ],
    aiTopic: 'Helping with your home move',
    aiIntro: {
      text: "I see you're moving a 2BR home — I can pre-brief the carrier on inventory if you share a few photos. It usually saves AED 60–120 in surprise charges and gets the right packing materials on the truck.",
      chips: [
        { label: 'Upload photos', next: 'upload' },
        { label: 'Take photos now', next: 'upload' },
        { label: 'Skip — use estimate', next: 'skip' },
      ],
      showUpload: true,
    },
    aiFollowUps: {
      uploadFirst:
        '👍 Detected: 3-seater sofa, coffee table, TV unit. Flagging 3 helpers + furniture dolly.',
      uploadSecond:
        '👍 Detected: queen bed (disassembly), wardrobe, dresser. +1 hour added to the estimate.',
      uploadThird:
        '✓ Inventory complete. Attached 3 photos + AI item list to your booking brief.',
      skipReply: {
        text: "No problem — I'll proceed with a standard 2BR estimate. Anything else to flag for the movers?",
        chips: [
          { label: 'Fragile items', next: 'fragile' },
          { label: 'Need packing supplies', next: 'help' },
          { label: "I'm good", next: 'done' },
        ],
      },
      defaultReply:
        "Noted — added to the carrier brief. They'll see this before confirming the job.",
    },
  },

  // ------------------------------------------------------ FURNITURE
  furniture: {
    id: 'furniture',
    name: 'Furniture Moving',
    shortName: 'Furniture',
    resultsHeading: 'Single-item movers nearby',
    resultsSubheading: 'Quick pickup & drop for furniture pieces',
    trucksNearby: 18,
    defaultPrompt: 'Move a sofa and dining table from JLT to Business Bay',
    summary: {
      pickup: 'JLT Cluster Y',
      pickupTime: 'Today, 03:00 PM',
      dropoff: 'Business Bay',
      dropoffTime: 'Est. arrival 03:50 PM',
      routeHeadline: 'JLT → Business Bay',
      vehicle: '1-Ton Pickup',
      vehicleNote: 'Sized for 2–4 furniture items',
      crewLabel: 'Required Helpers',
      crewValue: '1 Helper + Driver',
      crewNote: 'Padded straps & blankets included',
      baseFareLabel: 'Base Fare (1-Ton)',
      baseFare: 180,
      extraLabel: 'Helper (x1)',
      extra: 80,
      insurance: 30,
      total: 290,
      totalUpper: 340,
      insuranceNote: 'Includes item insurance up to AED 10,000.',
      primaryAction: 'Find Available Pickups',
    },
    suppliers: [
      {
        id: 1,
        name: 'QuickHaul Pickups',
        rating: 4.8,
        reviews: 612,
        eta: '8 mins',
        price: 290,
        truck: '1-Ton Pickup',
        verified: true,
        badge: 'Best Match',
        features: ['1 Helper', 'Padded Blankets', 'Strapping Kit'],
        image:
          'https://images.unsplash.com/photo-1519003722824-194d4455a60c?auto=format&fit=crop&q=80&w=400&h=250',
      },
      {
        id: 2,
        name: 'Marina Movers Lite',
        rating: 4.6,
        reviews: 220,
        eta: '14 mins',
        price: 260,
        truck: '1-Ton Pickup',
        verified: true,
        features: ['1 Helper', 'GPS Tracking'],
        image:
          'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?auto=format&fit=crop&q=80&w=400&h=250',
      },
      {
        id: 3,
        name: 'GoMove Express',
        rating: 4.9,
        reviews: 88,
        eta: '22 mins',
        price: 340,
        truck: '1.5-Ton Pickup',
        verified: true,
        features: ['2 Helpers', 'Disassembly Help', 'Premium Insurance'],
        image:
          'https://images.unsplash.com/photo-1586864387967-d02ef85d93e8?auto=format&fit=crop&q=80&w=400&h=250',
      },
    ],
    aiTopic: 'Helping with your furniture move',
    aiIntro: {
      text: 'Quick furniture pickups go smoother when carriers know dimensions. Want to share a photo of each item so I can confirm the 1-Ton pickup is the right fit?',
      chips: [
        { label: 'Upload photos', next: 'upload' },
        { label: 'Take photos', next: 'upload' },
        { label: 'Skip', next: 'skip' },
      ],
      showUpload: true,
    },
    aiFollowUps: {
      uploadFirst:
        '👍 Detected: 3-seater sofa, ~210cm wide. Confirms 1-Ton pickup is right.',
      uploadSecond:
        '✓ Detected: dining table + 4 chairs. Recommend strapping kit — already standard with this carrier.',
      uploadThird:
        '👍 Inventory locked. The carrier will arrive prepared with blankets, straps, and a dolly.',
      skipReply: {
        text: "All good. I'll keep the 1-Ton pickup default. Anything fragile or oversized?",
        chips: [
          { label: 'Glass top — fragile', next: 'fragile' },
          { label: 'Oversized item', next: 'help' },
          { label: 'Nothing special', next: 'done' },
        ],
      },
      defaultReply: 'Got it — added a note for the driver.',
    },
  },

  // ------------------------------------------------------ VEHICLE RECOVERY
  recovery: {
    id: 'recovery',
    name: 'Vehicle Recovery',
    shortName: 'Recovery',
    resultsHeading: 'Recovery trucks closest to you',
    resultsSubheading: '24/7 towing & roadside assistance — sorted by ETA',
    trucksNearby: 9,
    defaultPrompt: 'Need recovery for broken BMW in Marina',
    summary: {
      pickup: 'Dubai Marina (Pinned location)',
      pickupTime: 'ASAP — Now',
      dropoff: 'AutoCare Workshop, Al Quoz',
      dropoffTime: 'Est. arrival in 40 min',
      routeHeadline: 'Marina → Al Quoz workshop',
      vehicle: 'Flatbed Recovery Truck',
      vehicleNote: 'Safe for non-running cars & low-clearance',
      crewLabel: 'Recovery Operator',
      crewValue: '1 Certified Operator',
      crewNote: 'RTA-licensed, includes winching',
      baseFareLabel: 'Recovery Base',
      baseFare: 150,
      extraLabel: 'Distance (18 km)',
      extra: 90,
      insurance: 0,
      total: 240,
      totalUpper: 290,
      insuranceNote: 'Includes vehicle damage coverage up to AED 100,000.',
      primaryAction: 'Find Available Recovery Trucks',
    },
    suppliers: [
      {
        id: 1,
        name: 'Rapid Recovery 24/7',
        rating: 4.9,
        reviews: 1245,
        eta: '6 mins',
        price: 240,
        truck: 'Flatbed Recovery',
        verified: true,
        badge: 'Fastest',
        features: ['Flatbed', 'Winch Equipped', 'RTA Certified'],
        image:
          'https://images.unsplash.com/photo-1597007030739-6d2e7172ee6c?auto=format&fit=crop&q=80&w=400&h=250',
      },
      {
        id: 2,
        name: 'UAE Roadside Pros',
        rating: 4.8,
        reviews: 980,
        eta: '12 mins',
        price: 220,
        truck: 'Wheel Lift Tow',
        verified: true,
        features: ['Wheel Lift', 'Jump Start Kit', 'Insurance Backed'],
        image:
          'https://images.unsplash.com/photo-1605559424843-9e4c228bf1c2?auto=format&fit=crop&q=80&w=400&h=250',
      },
      {
        id: 3,
        name: 'Premium Auto Recovery',
        rating: 5.0,
        reviews: 411,
        eta: '18 mins',
        price: 320,
        truck: 'Enclosed Flatbed',
        verified: true,
        features: ['Enclosed', 'Premium Cars Specialist', 'Damage Coverage'],
        image:
          'https://images.unsplash.com/photo-1623013438264-d2a3aa0a4d6f?auto=format&fit=crop&q=80&w=400&h=250',
      },
    ],
    aiTopic: 'Helping with your recovery',
    aiIntro: {
      text: "Recovery operators arrive faster when they know the vehicle's condition. Can you share a quick photo of the car and its surroundings so they bring the right rig?",
      chips: [
        { label: 'Share vehicle photo', next: 'upload' },
        { label: 'Won’t start', next: 'help' },
        { label: 'Accident — need urgent', next: 'fragile' },
      ],
      showUpload: true,
    },
    aiFollowUps: {
      uploadFirst:
        '👍 Detected: BMW sedan, looks low-clearance. Confirms flatbed (not wheel-lift) — already matched.',
      uploadSecond:
        '✓ Got the surroundings. Operator will use Marina Gate 4 entrance. ETA holds at ~6 min.',
      uploadThird: '✓ Vehicle profile attached. Dispatch has full context.',
      skipReply: {
        text: "Understood — I've flagged this as urgent. Operator will call you on arrival. Any injuries or hazards?",
        chips: [
          { label: 'No injuries', next: 'done' },
          { label: 'Need ambulance', next: 'fragile' },
          { label: 'Fuel leak / hazard', next: 'fragile' },
        ],
      },
      defaultReply: 'Logged. Dispatch has been updated in real time.',
    },
  },

  // ------------------------------------------------------ CAR CARRIER
  carrier: {
    id: 'carrier',
    name: 'Car Carrier',
    shortName: 'Car Carrier',
    resultsHeading: 'Car carriers for your route',
    resultsSubheading: 'Intercity vehicle transport — open & enclosed options',
    trucksNearby: 6,
    defaultPrompt: 'Move my car from Dubai to Abu Dhabi',
    summary: {
      pickup: 'Dubai — Jumeirah',
      pickupTime: 'Tomorrow, 10:00 AM',
      dropoff: 'Abu Dhabi — Khalifa City',
      dropoffTime: 'Est. arrival 12:30 PM',
      routeHeadline: 'Dubai → Abu Dhabi',
      vehicle: 'Single Car Open Carrier',
      vehicleNote: 'Standard for sedan / SUV transport',
      crewLabel: 'Driver',
      crewValue: '1 Licensed Driver',
      crewNote: 'Loading & strapping handled',
      baseFareLabel: 'Intercity Base',
      baseFare: 280,
      extraLabel: 'Distance (140 km)',
      extra: 140,
      insurance: 40,
      total: 460,
      totalUpper: 540,
      insuranceNote: 'Includes vehicle transit insurance up to AED 200,000.',
      primaryAction: 'Find Available Carriers',
    },
    suppliers: [
      {
        id: 1,
        name: 'CityLink Auto Transport',
        rating: 4.9,
        reviews: 720,
        eta: '30 mins',
        price: 460,
        truck: 'Single Car Open',
        verified: true,
        badge: 'Best Match',
        features: ['Open Carrier', 'Door-to-door', 'Transit Insurance'],
        image:
          'https://images.unsplash.com/photo-1583121274602-3e2820c69888?auto=format&fit=crop&q=80&w=400&h=250',
      },
      {
        id: 2,
        name: 'GCC Auto Movers',
        rating: 4.7,
        reviews: 410,
        eta: '55 mins',
        price: 420,
        truck: 'Single Car Open',
        verified: true,
        features: ['Open Carrier', 'GPS Tracking', 'Standard Insurance'],
        image:
          'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?auto=format&fit=crop&q=80&w=400&h=250',
      },
      {
        id: 3,
        name: 'LuxLine Enclosed',
        rating: 5.0,
        reviews: 156,
        eta: '90 mins',
        price: 780,
        truck: 'Enclosed Carrier',
        verified: true,
        features: ['Enclosed', 'Luxury Specialist', 'Premium Insurance'],
        image:
          'https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?auto=format&fit=crop&q=80&w=400&h=250',
      },
    ],
    aiTopic: 'Helping with your car transport',
    aiIntro: {
      text: "Carriers price by vehicle size. Share a photo of the car (or the make/model) and I'll lock the right carrier type — open is standard, enclosed for luxury or low-clearance.",
      chips: [
        { label: 'Upload car photo', next: 'upload' },
        { label: 'It’s a sedan', next: 'help' },
        { label: 'Luxury — need enclosed', next: 'fragile' },
      ],
      showUpload: true,
    },
    aiFollowUps: {
      uploadFirst:
        '✓ Detected: standard sedan. Open carrier is right — saves AED 320 vs enclosed.',
      uploadSecond:
        '✓ Captured registration & condition. Attached to the carrier brief for handover.',
      uploadThird: '✓ All set. Vehicle profile locked.',
      skipReply: {
        text: 'Got it — open carrier confirmed. Want to add a pickup time window?',
        chips: [
          { label: 'Morning (8–11 AM)', next: 'done' },
          { label: 'Afternoon (12–4 PM)', next: 'done' },
          { label: 'Any time', next: 'done' },
        ],
      },
      defaultReply: 'Locked in. Carrier brief updated.',
    },
  },

  // ------------------------------------------------------ HEAVY / LOWBED
  heavy: {
    id: 'heavy',
    name: 'Heavy Equipment',
    shortName: 'Heavy / Lowbed',
    resultsHeading: 'Lowbed & heavy haulers',
    resultsSubheading: 'For equipment, machinery & oversize loads',
    trucksNearby: 4,
    defaultPrompt: 'Need lowbed for excavator from Sharjah to Al Ain site',
    summary: {
      pickup: 'Sharjah Industrial Area 12',
      pickupTime: 'Tomorrow, 06:00 AM',
      dropoff: 'Al Ain — Construction Site B7',
      dropoffTime: 'Est. arrival 11:00 AM',
      routeHeadline: 'Sharjah → Al Ain',
      vehicle: '3-Axle Lowbed (40 ton)',
      vehicleNote: 'Right for standard 20-ton excavators',
      crewLabel: 'Crew',
      crewValue: 'Driver + Loader',
      crewNote: 'Includes loading ramps & lashing',
      baseFareLabel: 'Lowbed Base',
      baseFare: 1400,
      extraLabel: 'Distance + Permits',
      extra: 600,
      insurance: 180,
      total: 2180,
      totalUpper: 2480,
      insuranceNote: 'Includes equipment transit cover up to AED 1M.',
      primaryAction: 'Find Available Lowbeds',
    },
    suppliers: [
      {
        id: 1,
        name: 'HeavyLift UAE',
        rating: 4.9,
        reviews: 218,
        eta: '60 mins',
        price: 2180,
        truck: '3-Axle Lowbed',
        verified: true,
        badge: 'Best Match',
        features: ['40-ton Capacity', 'Permits Handled', 'Lashing Included'],
        image:
          'https://images.unsplash.com/photo-1581094271901-8022df4466f9?auto=format&fit=crop&q=80&w=400&h=250',
      },
      {
        id: 2,
        name: 'Emirates Lowbed Co.',
        rating: 4.7,
        reviews: 142,
        eta: '95 mins',
        price: 2050,
        truck: '3-Axle Lowbed',
        verified: true,
        features: ['40-ton Capacity', 'GPS Tracking', 'Standard Cover'],
        image:
          'https://images.unsplash.com/photo-1571864091183-eb6a14d72d8e?auto=format&fit=crop&q=80&w=400&h=250',
      },
      {
        id: 3,
        name: 'MegaHaul Specialists',
        rating: 5.0,
        reviews: 67,
        eta: '2.5 hrs',
        price: 2580,
        truck: '5-Axle Extendable',
        verified: true,
        features: ['Up to 80-ton', 'Escort Vehicle', 'Premium Insurance'],
        image:
          'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?auto=format&fit=crop&q=80&w=400&h=250',
      },
    ],
    aiTopic: 'Helping with your heavy haul',
    aiIntro: {
      text: "For heavy haul I need a few specifics: equipment weight, width, and whether oversize permits are needed. Want to share the equipment's spec sheet or a photo?",
      chips: [
        { label: 'Upload spec sheet', next: 'upload' },
        { label: 'Excavator ~20 ton', next: 'help' },
        { label: 'Oversize — need permit', next: 'fragile' },
      ],
      showUpload: true,
    },
    aiFollowUps: {
      uploadFirst:
        '👍 Detected: 20-ton excavator, 2.9m wide. Within standard permit limits.',
      uploadSecond:
        '✓ Got the site access photo. Lowbed will use Gate B for loading.',
      uploadThird: '✓ All specs locked. Permit application auto-filed.',
      skipReply: {
        text: "Noted — 20-ton excavator on a standard lowbed. I'll auto-file the route permit. Any time constraint?",
        chips: [
          { label: 'Early morning only', next: 'done' },
          { label: 'Flexible', next: 'done' },
        ],
      },
      defaultReply: 'Updated the haul brief.',
    },
  },

  // ------------------------------------------------------ CHILLER
  chiller: {
    id: 'chiller',
    name: 'Chiller Truck',
    shortName: 'Chiller',
    resultsHeading: 'Refrigerated trucks nearby',
    resultsSubheading: 'Temperature-controlled cargo delivery',
    trucksNearby: 7,
    defaultPrompt:
      'Need a chiller truck for frozen goods from JAFZA to Sharjah',
    summary: {
      pickup: 'JAFZA Block 7',
      pickupTime: 'Today, 04:00 PM',
      dropoff: 'Sharjah — Industrial 4',
      dropoffTime: 'Est. arrival 05:20 PM',
      routeHeadline: 'JAFZA → Sharjah',
      vehicle: '3-Ton Chiller Truck',
      vehicleNote: 'Holds –18°C to +4°C',
      crewLabel: 'Driver',
      crewValue: '1 Cold-chain Trained',
      crewNote: 'Pre-cool ready, food-grade certified',
      baseFareLabel: 'Chiller Base',
      baseFare: 380,
      extraLabel: 'Cold-chain surcharge',
      extra: 120,
      insurance: 60,
      total: 560,
      totalUpper: 640,
      insuranceNote: 'Includes perishables cover up to AED 50,000.',
      primaryAction: 'Find Available Chillers',
    },
    suppliers: [
      {
        id: 1,
        name: 'ColdLink Logistics',
        rating: 4.9,
        reviews: 386,
        eta: '20 mins',
        price: 560,
        truck: '3-Ton Chiller',
        verified: true,
        badge: 'Best Match',
        features: ['–18°C to +4°C', 'Food Grade', 'Live Temp Logging'],
        image:
          'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?auto=format&fit=crop&q=80&w=400&h=250',
      },
      {
        id: 2,
        name: 'PolarFleet UAE',
        rating: 4.6,
        reviews: 198,
        eta: '32 mins',
        price: 510,
        truck: '3-Ton Chiller',
        verified: true,
        features: ['+2°C to +8°C', 'GPS Tracking', 'Standard Cover'],
        image:
          'https://images.unsplash.com/photo-1519003722824-194d4455a60c?auto=format&fit=crop&q=80&w=400&h=250',
      },
      {
        id: 3,
        name: 'ArcticHaul Premium',
        rating: 5.0,
        reviews: 91,
        eta: '50 mins',
        price: 720,
        truck: '5-Ton Freezer',
        verified: true,
        features: ['–25°C Deep Freeze', 'HACCP Certified', 'Premium Insurance'],
        image:
          'https://images.unsplash.com/photo-1586864387967-d02ef85d93e8?auto=format&fit=crop&q=80&w=400&h=250',
      },
    ],
    aiTopic: 'Helping with your cold-chain run',
    aiIntro: {
      text: 'Cold-chain pricing depends on target temperature. What range do you need — chilled (+2°C to +8°C), frozen (−18°C), or deep-freeze (−25°C)?',
      chips: [
        { label: 'Chilled +2 to +8°C', next: 'help' },
        { label: 'Frozen –18°C', next: 'help' },
        { label: 'Deep freeze –25°C', next: 'fragile' },
      ],
    },
    aiFollowUps: {
      uploadFirst:
        '✓ Cargo photo received. Confirmed cold-chain packaging is intact.',
      uploadSecond: '✓ Logged second photo. Pre-cool will start before pickup.',
      uploadThird: '✓ Cold-chain profile attached to the carrier brief.',
      skipReply: {
        text: 'Locked in. Want live temperature logging shared with you in real time?',
        chips: [
          { label: 'Yes — send updates', next: 'done' },
          { label: 'Not needed', next: 'done' },
        ],
      },
      defaultReply: 'Updated the cold-chain brief.',
    },
  },

  // ------------------------------------------------------ GOODS / COMMERCIAL
  goods: {
    id: 'goods',
    name: 'Goods Transport',
    shortName: 'Goods',
    resultsHeading: 'Commercial cargo trucks',
    resultsSubheading: 'Flatbeds, curtain-sides & box trucks',
    trucksNearby: 14,
    defaultPrompt: 'Move pallets from JAFZA to a Sharjah warehouse',
    summary: {
      pickup: 'JAFZA Warehouse 12',
      pickupTime: 'Today, 02:00 PM',
      dropoff: 'Sharjah — Industrial 6',
      dropoffTime: 'Est. arrival 03:40 PM',
      routeHeadline: 'JAFZA → Sharjah Industrial',
      vehicle: '7-Ton Box Truck',
      vehicleNote: 'Fits ~10 standard pallets',
      crewLabel: 'Loader Crew',
      crewValue: 'Driver + 1 Loader',
      crewNote: 'Pallet jack on board',
      baseFareLabel: 'Cargo Base',
      baseFare: 320,
      extraLabel: 'Loader (x1)',
      extra: 100,
      insurance: 40,
      total: 460,
      totalUpper: 540,
      insuranceNote: 'Includes cargo cover up to AED 75,000.',
      primaryAction: 'Find Available Cargo Trucks',
    },
    suppliers: [
      {
        id: 1,
        name: 'CargoFlow Express',
        rating: 4.8,
        reviews: 540,
        eta: '18 mins',
        price: 460,
        truck: '7-Ton Box',
        verified: true,
        badge: 'Best Match',
        features: ['Pallet Jack', '1 Loader', 'GPS Tracking'],
        image:
          'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?auto=format&fit=crop&q=80&w=400&h=250',
      },
      {
        id: 2,
        name: 'Emirates Freight Co.',
        rating: 4.6,
        reviews: 312,
        eta: '28 mins',
        price: 420,
        truck: '5-Ton Curtain-side',
        verified: true,
        features: ['Easy Loading', 'Tarp Cover', 'Standard Insurance'],
        image:
          'https://images.unsplash.com/photo-1519003722824-194d4455a60c?auto=format&fit=crop&q=80&w=400&h=250',
      },
      {
        id: 3,
        name: 'GulfHaul Premium',
        rating: 4.9,
        reviews: 178,
        eta: '40 mins',
        price: 580,
        truck: '10-Ton Box',
        verified: true,
        features: ['2 Loaders', 'Hydraulic Lift', 'Premium Insurance'],
        image:
          'https://images.unsplash.com/photo-1586864387967-d02ef85d93e8?auto=format&fit=crop&q=80&w=400&h=250',
      },
    ],
    aiTopic: 'Helping with your cargo run',
    aiIntro: {
      text: 'To right-size the truck, I need the load type and pallet count. How many pallets and is anything fragile or hazardous?',
      chips: [
        { label: '5–10 standard pallets', next: 'help' },
        { label: '10+ pallets', next: 'help' },
        { label: 'Contains fragile items', next: 'fragile' },
      ],
    },
    aiFollowUps: {
      uploadFirst:
        '✓ Photo received. Detected ~8 stacked pallets. 7-Ton box is the right fit.',
      uploadSecond:
        '✓ Got the warehouse dock photo. Carrier will use Dock 4 for loading.',
      uploadThird: '✓ Load profile attached. Carrier confirmed.',
      skipReply: {
        text: 'Locked in. Need a tail-lift truck for ground-level loading?',
        chips: [
          { label: 'Yes — no dock', next: 'help' },
          { label: 'Dock available', next: 'done' },
        ],
      },
      defaultReply: 'Updated the load brief.',
    },
  },

  // ------------------------------------------------------ CONSTRUCTION
  construction: {
    id: 'construction',
    name: 'Construction Logistics',
    shortName: 'Construction',
    resultsHeading: 'Construction haulers nearby',
    resultsSubheading: 'Tippers, flatbeds & material delivery',
    trucksNearby: 8,
    defaultPrompt: 'Need flatbed for steel beams to Business Bay site',
    summary: {
      pickup: 'Dubai Investment Park',
      pickupTime: 'Tomorrow, 07:00 AM',
      dropoff: 'Business Bay Site C',
      dropoffTime: 'Est. arrival 08:30 AM',
      routeHeadline: 'DIP → Business Bay',
      vehicle: '20-Ton Flatbed',
      vehicleNote: 'Suited for long steel beams (up to 12m)',
      crewLabel: 'Crew',
      crewValue: 'Driver + 1 Rigger',
      crewNote: 'Lashing & flag protocol included',
      baseFareLabel: 'Flatbed Base',
      baseFare: 780,
      extraLabel: 'Rigger + Lashing',
      extra: 220,
      insurance: 90,
      total: 1090,
      totalUpper: 1280,
      insuranceNote: 'Includes site & cargo cover up to AED 500,000.',
      primaryAction: 'Find Available Haulers',
    },
    suppliers: [
      {
        id: 1,
        name: 'BuildHaul UAE',
        rating: 4.8,
        reviews: 264,
        eta: '40 mins',
        price: 1090,
        truck: '20-Ton Flatbed',
        verified: true,
        badge: 'Best Match',
        features: ['Lashing Kit', 'Rigger Included', 'Site-safety Trained'],
        image:
          'https://images.unsplash.com/photo-1581094271901-8022df4466f9?auto=format&fit=crop&q=80&w=400&h=250',
      },
      {
        id: 2,
        name: 'SiteLink Logistics',
        rating: 4.6,
        reviews: 188,
        eta: '60 mins',
        price: 980,
        truck: '15-Ton Flatbed',
        verified: true,
        features: ['Lashing Kit', 'GPS Tracking', 'Standard Cover'],
        image:
          'https://images.unsplash.com/photo-1571864091183-eb6a14d72d8e?auto=format&fit=crop&q=80&w=400&h=250',
      },
      {
        id: 3,
        name: 'Pinnacle Site Haul',
        rating: 4.9,
        reviews: 102,
        eta: '90 mins',
        price: 1320,
        truck: '25-Ton Extendable',
        verified: true,
        features: ['Long Beam (15m)', '2 Riggers', 'Premium Insurance'],
        image:
          'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?auto=format&fit=crop&q=80&w=400&h=250',
      },
    ],
    aiTopic: 'Helping with your site delivery',
    aiIntro: {
      text: "Site deliveries depend on load length and site access. What's the longest piece, and is the site gate height OK for a 20-ton flatbed?",
      chips: [
        { label: 'Beams up to 12m', next: 'help' },
        { label: 'Site access photo', next: 'upload' },
        { label: 'Need extendable trailer', next: 'fragile' },
      ],
      showUpload: true,
    },
    aiFollowUps: {
      uploadFirst:
        '✓ Detected 8m steel beams. 20-Ton flatbed is the right call.',
      uploadSecond:
        '✓ Site access photo logged. Confirmed gate clearance ≥ 4m.',
      uploadThird: '✓ Site profile saved. Driver briefed.',
      skipReply: {
        text: 'Locked in. Need this scheduled as a recurring delivery for the project?',
        chips: [
          { label: 'Yes — weekly', next: 'done' },
          { label: 'One-off only', next: 'done' },
        ],
      },
      defaultReply: 'Updated the site brief.',
    },
  },

  // ------------------------------------------------------ BUSINESS
  business: {
    id: 'business',
    name: 'Business Deliveries',
    shortName: 'B2B',
    resultsHeading: 'B2B carrier partners',
    resultsSubheading: 'Account-based logistics for recurring runs',
    trucksNearby: 22,
    defaultPrompt: 'Set up daily route from warehouse to 5 retail stores',
    summary: {
      pickup: 'Al Quoz Warehouse',
      pickupTime: 'Daily, 08:00 AM',
      dropoff: '5 retail drop points',
      dropoffTime: 'Est. complete by 02:00 PM',
      routeHeadline: 'Al Quoz → 5-stop retail route',
      vehicle: '3-Ton Box (Recurring)',
      vehicleNote: 'Dedicated truck assigned',
      crewLabel: 'Driver',
      crewValue: 'Dedicated Driver',
      crewNote: 'Same driver across the contract',
      baseFareLabel: 'Per-route Rate',
      baseFare: 420,
      extraLabel: 'Multi-stop (x4)',
      extra: 160,
      insurance: 50,
      total: 630,
      totalUpper: 720,
      insuranceNote: 'Includes account-level cargo cover.',
      primaryAction: 'Find B2B Partners',
    },
    suppliers: [
      {
        id: 1,
        name: 'Acme Distribution Co.',
        rating: 4.9,
        reviews: 412,
        eta: 'Next-day start',
        price: 630,
        truck: '3-Ton Box (Recurring)',
        verified: true,
        badge: 'Best Match',
        features: ['Dedicated Driver', 'POD Per Stop', 'API Access'],
        image:
          'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?auto=format&fit=crop&q=80&w=400&h=250',
      },
      {
        id: 2,
        name: 'GulfChain Logistics',
        rating: 4.7,
        reviews: 268,
        eta: 'Next-day start',
        price: 590,
        truck: '3-Ton Box',
        verified: true,
        features: ['Multi-stop Routing', 'GPS Tracking', 'Monthly Invoicing'],
        image:
          'https://images.unsplash.com/photo-1519003722824-194d4455a60c?auto=format&fit=crop&q=80&w=400&h=250',
      },
      {
        id: 3,
        name: 'Pair2 Verified Network',
        rating: 5.0,
        reviews: 802,
        eta: '48-hr setup',
        price: 720,
        truck: 'Mixed Fleet',
        verified: true,
        features: ['Multi-truck', 'Control Tower Access', 'SLA Guaranteed'],
        image:
          'https://images.unsplash.com/photo-1586864387967-d02ef85d93e8?auto=format&fit=crop&q=80&w=400&h=250',
      },
    ],
    aiTopic: 'Helping with your B2B setup',
    aiIntro: {
      text: 'Recurring runs are cheaper when bundled. How many drop points per day, and do you need POD per stop?',
      chips: [
        { label: 'Up to 5 stops/day', next: 'help' },
        { label: '6–10 stops/day', next: 'help' },
        { label: 'POD per stop required', next: 'fragile' },
      ],
    },
    aiFollowUps: {
      uploadFirst:
        '✓ Route file received. Detected 5 stops, ~120 km/day. 3-Ton truck is right.',
      uploadSecond:
        '✓ Got the second route — option to merge for a single truck across both.',
      uploadThird: '✓ B2B contract draft ready for review.',
      skipReply: {
        text: 'Locked in. Should we set you up with API access for shipment creation?',
        chips: [
          { label: 'Yes — give me API', next: 'done' },
          { label: 'Manual is fine', next: 'done' },
        ],
      },
      defaultReply: 'Updated your account brief.',
    },
  },
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

const PROMPT_KEYWORDS: { id: ServiceId; keywords: string[] }[] = [
  {
    id: 'recovery',
    keywords: [
      'recovery',
      'tow',
      'towing',
      'broken',
      'breakdown',
      'accident',
      'won’t start',
      'wont start',
      'jump start',
    ],
  },
  {
    id: 'carrier',
    keywords: [
      'car carrier',
      'move my car',
      'car from',
      'sedan from',
      'suv from',
      'transport my car',
    ],
  },
  {
    id: 'home',
    keywords: [
      'home',
      'apartment',
      'villa',
      'bedroom',
      'br ',
      '1br',
      '2br',
      '3br',
      'studio',
      'flat',
    ],
  },
  {
    id: 'furniture',
    keywords: [
      'furniture',
      'sofa',
      'couch',
      'bed',
      'dining table',
      'wardrobe',
      'cabinet',
      'mattress',
    ],
  },
  {
    id: 'heavy',
    keywords: [
      'lowbed',
      'excavator',
      'bulldozer',
      'crane',
      'forklift',
      'heavy equipment',
      'machinery',
      'oversize',
    ],
  },
  {
    id: 'chiller',
    keywords: [
      'chiller',
      'frozen',
      'cold',
      'refrigerated',
      'cold chain',
      'perishable',
      'ice cream',
      'dairy',
    ],
  },
  {
    id: 'construction',
    keywords: [
      'construction',
      'site',
      'steel',
      'cement',
      'beam',
      'rebar',
      'concrete',
      'pipes',
    ],
  },
  {
    id: 'goods',
    keywords: [
      'pallet',
      'cargo',
      'goods',
      'warehouse',
      'shipment',
      'b2b cargo',
    ],
  },
  {
    id: 'business',
    keywords: [
      'recurring',
      'daily route',
      'multi-stop',
      'multiple stores',
      'b2b',
    ],
  },
]

export function detectServiceFromPrompt(prompt: string): ServiceId {
  const lower = prompt.toLowerCase()
  let bestId: ServiceId = 'home'
  let bestScore = 0
  for (const rule of PROMPT_KEYWORDS) {
    let score = 0
    for (const k of rule.keywords) {
      const escaped = k.replace(/[.*+?^${}()|[\]\\]/g, (m) => '\\' + m)
      const isAlnum = /^[a-z0-9]+$/i.test(k.replace(/\s/g, ''))
      const re = isAlnum
        ? new RegExp('\\b' + escaped + '\\b', 'i')
        : new RegExp('(^|\\s)' + escaped + '(\\s|$|[.,!?])', 'i')
      if (re.test(lower)) score++
    }
    if (score > bestScore) {
      bestScore = score
      bestId = rule.id
    }
  }
  return bestScore > 0 ? bestId : 'home'
}

// Light persistence so context survives across pages even if route state is lost.
const STORAGE_KEY = 'pair2.activeService'
const PROMPT_KEY = 'pair2.activePrompt'

export function setActiveService(id: ServiceId, prompt?: string) {
  try {
    sessionStorage.setItem(STORAGE_KEY, id)
    if (prompt) sessionStorage.setItem(PROMPT_KEY, prompt)
  } catch {
    // ignore (SSR / disabled storage)
  }
}

export function getActiveService(): ServiceId {
  try {
    const v = sessionStorage.getItem(STORAGE_KEY) as ServiceId | null
    if (v && SERVICE_DATA[v]) return v
  } catch {
    // ignore
  }
  return 'home'
}

export function getActivePrompt(): string | null {
  try {
    return sessionStorage.getItem(PROMPT_KEY)
  } catch {
    return null
  }
}

export function getServiceConfig(id?: ServiceId | null): ServiceConfig {
  if (id && SERVICE_DATA[id]) return SERVICE_DATA[id]
  return SERVICE_DATA[getActiveService()]
}
